import {
  Spinner,
  VerticalSlider,
  HorizontalSlider,
  IconButton,
  BlindsOpen,
  BlindsClose,
  BlindsUp,
  BlindsDown,
  Favourite,
} from '@mgh-app/component-library';
import { useState, useEffect } from 'react';
import { useGetShadesValuesQuery, useSetShadesValueMutation } from 'services/ThingsService';
import { POLLING_INTERVAL } from 'services/apiConfig';
import {
  alignItems,
  backgroundColor,
  borderRadius,
  boxShadow,
  display,
  flexDirection,
  fontSize,
  gap,
  gridTemplateRows,
  height,
  inset,
  justifyContent,
  margin,
  maxHeight,
  maxWidth,
  padding,
  pointerEvents,
  position,
  tailwindClassNameConvertNegative,
  textColor,
  TMargin,
  TTranslate,
  twCls,
  width,
} from 'style';

import blindsBackground from 'assets/blindsBackground.svg?inline';

const invertValue = (value: number) => 100 - value;

export interface BlindSetControllerProps {
  blindSetId: string;
}

export const BlindSetController = ({ blindSetId }: BlindSetControllerProps): JSX.Element => {
  const {
    data: blind,
    isLoading,
    isError,
  } = useGetShadesValuesQuery(blindSetId, {
    pollingInterval: POLLING_INTERVAL,
    refetchOnMountOrArgChange: true,
  });
  const [setBlindValue] = useSetShadesValueMutation();

  const [blindPosition, setBlindPosition] = useState<number>(0);
  const [favourite, setFavourite] = useState<number>(0);

  const onPositionChange = (_: Event, newValue: number | number[]) => {
    const value = Array.isArray(newValue) ? newValue[0] : newValue;
    setBlindPosition(value);
  };

  const onPositionChangeCommitted = (newValue: number | number[]) => {
    if (blind) {
      const value = Array.isArray(newValue) ? newValue[0] : newValue;
      setBlindValue({ ...blind, position: invertValue(value), favourite: favourite });
    }
  };

  const openBlinds = () => {
    if (blind) {
      setBlindPosition(100);
      setBlindValue({ ...blind, position: 0, favourite: favourite });
    }
  };

  const closeBlinds = () => {
    if (blind) {
      setBlindPosition(0);
      setBlindValue({ ...blind, position: 100, favourite: favourite });
    }
  };

  const moveBlindsDown = () => {
    if (blind && blindPosition > 0) {
      const newPosition = blindPosition - 1;
      setBlindPosition(newPosition);
      setBlindValue({ ...blind, position: invertValue(newPosition), favourite: favourite });
    }
  };

  const moveBlindsUp = () => {
    if (blind && blindPosition < 100) {
      const newPosition = blindPosition + 1;
      setBlindPosition(newPosition);
      setBlindValue({ ...blind, position: invertValue(newPosition), favourite: favourite });
    }
  };

  const onFavouriteChange = (_: Event, newValue: number | number[]) => {
    const value = Array.isArray(newValue) ? newValue[0] : newValue;
    setFavourite(value);
  };

  const onFavouriteChangeCommitted = (newValue: number | number[]) => {
    if (blind) {
      const value = Array.isArray(newValue) ? newValue[0] : newValue;
      setBlindValue({ ...blind, position: invertValue(blindPosition), favourite: value });
    }
  };

  const setFavouritePosition = () => {
    if (blind) {
      setBlindPosition(invertValue(favourite));
      setBlindValue({ ...blind, position: favourite, favourite: favourite });
    }
  };

  useEffect(() => {
    if (blind) {
      setBlindPosition(invertValue(blind.position));
      setFavourite(blind.favourite);
    }
  }, [blind]);

  return (
    <>
      {isError ? (
        <>Could not load blinds</>
      ) : !isLoading && blind ? (
        <div className={twCls(display('grid'), justifyContent('justify-center'), padding('px-4'))}>
          <div
            className={twCls(
              display('grid'),
              gridTemplateRows('grid-rows-fc-min-2'),
              flexDirection('flex-col'),
              width('w-11/12'),
              maxWidth('max-w-xs'),
            )}
          >
            <div
              className={twCls(
                display('grid'),
                maxHeight('max-h-96'),
                boxShadow('shadow-shades-outer'),
              )}
            >
              <div className={twCls(position('relative'), display('grid'))}>
                <VerticalSlider
                  sx={{
                    color: 'white',
                    width: '100%',
                    height: '100%',
                    boxSizing: 'border-box',
                    '& .MuiSlider-track': {
                      borderColor: 'white',
                      borderWidth:
                        blindPosition === 100
                          ? '0.5rem 0.5rem 0.5rem 0.5rem'
                          : blindPosition === 0
                          ? '0'
                          : '0rem 0.5rem 0.5rem 0.5rem',
                      borderRadius: 0,
                      backgroundColor: 'white',
                      boxShadow: 'inset 0pt 0pt 14pt 8pt rgba(87,127,255,0.15)',
                    },
                    '& .MuiSlider-rail': {
                      borderColor: 'transparent',
                      borderRadius: 0,
                      backgroundImage: `url("${blindsBackground}")`,
                      backgroundPosition: 'center',
                      backgroundSize: 'cover',
                      backgroundRepeat: 'no-repeat',
                    },
                    '& .MuiSlider-thumb': {
                      display: 'none',
                    },
                  }}
                  valueLabelDisplay="off"
                  track="inverted"
                  scale={(x) => 100 - x}
                  value={blindPosition}
                  onChange={onPositionChange}
                  onChangeCommitted={(_, val) => onPositionChangeCommitted(val)}
                />
                <div
                  className={twCls(
                    position('absolute'),
                    inset('top-1/2', 'left-1/2'),
                    tailwindClassNameConvertNegative<TTranslate>('-translate-x-1/2'),
                    tailwindClassNameConvertNegative<TTranslate>('-translate-y-1/2'),
                    display('flex'),
                    flexDirection('flex-col'),
                    alignItems('items-center'),
                    gap('gap-y-4'),
                    pointerEvents('pointer-events-none'),
                  )}
                >
                  <div
                    className={twCls(
                      height('h-20'),
                      width('w-20'),
                      backgroundColor('bg-white-opacity'),
                      borderRadius('rounded-full'),
                      textColor('text-blue-100'),
                      fontSize('text-3xl'),
                      display('flex'),
                      alignItems('items-center'),
                      justifyContent('justify-center'),
                    )}
                  >
                    {invertValue(blindPosition)}%
                  </div>
                  <div
                    className={twCls(
                      width('w-max'),
                      padding('px-3'),
                      backgroundColor('bg-white-opacity'),
                      borderRadius('rounded-xl'),
                      textColor('text-grey-700'),
                      fontSize('text-base'),
                      display('flex'),
                      alignItems('items-center'),
                      justifyContent('justify-center'),
                    )}
                  >
                    Drag up and down to control
                  </div>
                </div>
              </div>
            </div>

            <div
              className={twCls(
                display('flex'),
                justifyContent('justify-between'),
                margin('mt-4'),
                tailwindClassNameConvertNegative<TMargin>('-mx-2'),
              )}
            >
              <IconButton onClick={openBlinds} className={twCls(padding('p-0'))}>
                <BlindsOpen />
              </IconButton>
              <IconButton onClick={closeBlinds} className={twCls(padding('p-0'))}>
                <BlindsClose />
              </IconButton>
              <IconButton
                onClick={moveBlindsUp}
                className={twCls(padding('p-0'))}
                disabled={blindPosition === 100}
              >
                <BlindsUp />
              </IconButton>
              <IconButton
                onClick={moveBlindsDown}
                className={twCls(padding('p-0'))}
                disabled={blindPosition === 0}
              >
                <BlindsDown />
              </IconButton>
            </div>

            <div
              className={twCls(
                display('flex'),
                alignItems('items-center'),
                margin('mt-4'),
                tailwindClassNameConvertNegative<TMargin>('-mx-2'),
              )}
            >
              <IconButton
                className={twCls(margin('mt-1'), padding('p-0'))}
                onClick={setFavouritePosition}
              >
                <Favourite />
              </IconButton>
              <HorizontalSlider
                className="mx-2"
                value={favourite}
                onChange={onFavouriteChange}
                onChangeCommitted={(_, val) => onFavouriteChangeCommitted(val)}
              />
              <span className={twCls(textColor('text-blue-100'), fontSize('text-xl'))}>
                {favourite}%
              </span>
            </div>
          </div>
        </div>
      ) : (
        <Spinner />
      )}
    </>
  );
};
